import { log } from '@domgen/shared/utils/log';
import posthog from 'posthog-js';
import { setAllPersonProfilePropertiesAsPersonPropertiesForFlags } from 'posthog-js/lib/src/customizations/index.js';
import {
  pageviewTracker,
  registerPageviewLifecycleListeners,
  windowPageviewLocation,
} from './pageviewTracker';

/**
 * @description Initializes PostHog analytics with the provided brand and source information. This function checks for the necessary environment variables and sets up PostHog with appropriate configurations, including feature flag handling and user property registration.
 * @param {Object} params - The parameters for initializing PostHog.
 * @param {string} params.brand - The brand associated with the analytics data.
 * @param {string} params.source - The source of the analytics data.
 * @param {string} [params.host] - Optional host information for additional context in analytics.
 * @param {string} [params.key] - Optional API key for PostHog initialization.
 * @returns {Promise<void>} A promise that resolves when PostHog is successfully initialized, or rejects if there is an error during initialization.
 * @throws {Error} Throws an error if the PostHog API key or host is not defined in the environment variables.
 */
export const initPostHog = ({
  brand,
  source,
  host,
  key,
}: {
  brand: string;
  source: string;
  host?: string;
  key?: string;
}): Promise<void> => {
  log.debug({ message: 'Initializing PostHog', data: { brand, source } });

  return new Promise<void>((resolve, reject) => {
    if (!key || !host) {
      return reject(new Error('PostHog API key or host is not defined in environment variables'));
    }

    // Pageviews are sent by the pageview tracker once each page has rendered (after any experiment
    // exposure). Registered before init so the pending pageview is sent before $pageleave on tab close.
    registerPageviewLifecycleListeners(pageviewTracker);

    posthog.init(key, {
      api_host: host,
      ui_host: 'https://eu.posthog.com',
      person_profiles: 'identified_only',
      capture_pageview: false,
      capture_pageleave: true,
      persistence: 'localStorage',
      defaults: '2026-01-30',
      loaded: (ph) => {
        // On feature flags gets invoked even if the flags request is successful or unsuccessful, so we can use it to ensure that PostHog has finished its initialization process
        // Complete in 3 seconds if the flags request fails to prevent hanging the app indefinitely
        setAllPersonProfilePropertiesAsPersonPropertiesForFlags(ph);

        ph.register({
          brand,
          source,
        });

        // Make brand and source available as person properties for flag release conditions
        ph.setPersonProperties({ brand, source });
        ph.setPersonPropertiesForFlags({ brand, source });

        // Where PostHog sent the first $pageview when it captured pageviews itself
        pageviewTracker.startVisit(windowPageviewLocation());

        ph.onFeatureFlags(() => {
          resolve();
        });
      },
    });
  });
};
